import { withResponseType } from '@warp-drive/core/request';
import type { ReactiveDataDocument } from '@warp-drive/core/reactive';
import { query } from '@warp-drive/utilities/json-api';
import type { AuthenticationLog } from '../data/resources/authentication-log.type.ts';
import type { NotificationHistoryExtension } from '../data/resources/notification-history.ext.ts';
import type { RemoteEvent } from '../data/resources/remote-event.type.ts';
import { userAggregateRequest, type UserAggregateResponse } from './user.ts';

type PaginatedHistory<T> = Omit<ReactiveDataDocument<T[]>, 'meta'> & {
  meta?: { page?: { total?: number } };
};

function requireHistoryId(id: string) {
  if (typeof id !== 'string' || id.trim().length === 0) {
    throw new TypeError('Profile history requires a persisted ID');
  }
}

function historyPagination(page: number, limit: number) {
  if (!Number.isSafeInteger(page) || page < 1) {
    throw new RangeError('Profile history page must be a positive safe integer');
  }
  if (!Number.isSafeInteger(limit) || limit < 1) {
    throw new RangeError('Profile history limit must be a positive safe integer');
  }

  const offset = (page - 1) * limit;
  if (!Number.isSafeInteger(offset)) {
    throw new RangeError('Profile history offset must be a safe integer');
  }

  return { 'page[limit]': limit, 'page[offset]': offset };
}

/**
 * Build a GET collection query whose records are materialized by the JSON:API cache.
 * @param userId persisted user whose authentication attempts are included
 * @param page one-based page number; must be a positive safe integer
 * @param limit maximum rows per page; must be a positive safe integer
 * @returns cacheable GET request for newest-first logs and optional page totals
 * @throws TypeError when the user ID is missing or blank
 * @throws RangeError when page or limit is invalid, or their computed offset is not a safe integer
 */
export function authenticationLogsRequest(userId: string, page = 1, limit = 10) {
  requireHistoryId(userId);

  return withResponseType<PaginatedHistory<AuthenticationLog>>(
    query<AuthenticationLog>('authentication-log', {
      'filter[user]': userId,
      ...historyPagination(page, limit),
      sort: '-created-at',
    })
  );
}

/**
 * Build a GET collection query whose records are materialized by the JSON:API cache.
 * @param userId persisted user whose remote events are included
 * @param limit maximum rows in the newest-events window; must be a positive safe integer
 * @returns cacheable GET request for newest events with a non-null event type
 * @throws TypeError when the user ID is missing or blank
 * @throws RangeError when limit is not a positive safe integer
 */
export function remoteEventsRequest(userId: string, limit = 10) {
  requireHistoryId(userId);

  return query<RemoteEvent>('remote-event', {
    'filter[user]': userId,
    'filter[event_type_not_null]': 1,
    ...historyPagination(1, limit),
    sort: '-at',
  });
}

/**
 * Build a GET collection query whose records are materialized by the JSON:API cache.
 * @param employeeId persisted employee whose delivered notifications are included
 * @param page one-based page number; must be a positive safe integer
 * @param limit maximum rows per page; must be a positive safe integer
 * @param noteSubtypeFilter exact notification subtype; an empty string includes every subtype
 * @returns cacheable GET request for newest-first notifications and optional page totals
 * @throws TypeError when the employee ID is missing or blank
 * @throws RangeError when page or limit is invalid, or their computed offset is not a safe integer
 */
export function notificationHistoriesRequest(
  employeeId: string,
  page = 1,
  limit = 10,
  noteSubtypeFilter = ''
) {
  requireHistoryId(employeeId);

  const params: Record<string, string | number> = {
    'filter[employee]': employeeId,
    ...historyPagination(page, limit),
    sort: '-created_at',
  };
  if (noteSubtypeFilter) {
    params['filter[noteSubtype]'] = noteSubtypeFilter;
  }

  return withResponseType<PaginatedHistory<NotificationHistoryExtension>>(
    query<NotificationHistoryExtension>('notification-history', params)
  );
}

/**
 * Build a GET notification-count aggregate using the user's accessible data scope.
 * @param userId persisted user whose notification counts are grouped by subtype
 * @returns uncached GET request for raw subtype counts, bypassing JSON:API materialization
 * @throws TypeError when the user ID is missing or blank
 */
export function notificationHistoryChartRequest(userId: string) {
  requireHistoryId(userId);

  return withResponseType<UserAggregateResponse>(
    userAggregateRequest(userId, {
      model: 'notification-history',
      groupBy: 'noteSubtype',
      rollups: { value: 'count()' },
    })
  );
}
